/**
 * Incoming parts — a unified view over what's actually arriving:
 *
 *   - Open purchase order lines (public.purchase_order_item) with
 *     remaining quantity (kind="po"). Optional MRP-only delivery splits
 *     (mrp.purchase_order_item_delivery) expand one PO line into several incoming rows
 *     with separate expected dates. Receiving is done outside this app; the list
 *     updates when the database reflects new received quantities. The API omits
 *     service-type SKUs and known non-material lines (Discount, Prototype, VALUE-ADD, Fee).
 *
 *   - Manual arrivals (transfers, samples, manual replacements) sourced from
 *     mrp.incoming_part (kind="non_po"). Tagged with a clear badge and
 *     editable in place.
 *
 *   - Work order rows (kind="work_order") default to the finished good on `public.build_order` (remaining build
 *     quantity from Megaventory columns). Pass `work_order_incoming_mode=components` on the API to list BOM material
 *     lines instead (used by kitting). Expected date rules: batch manufacturer ship-by, then hub override, then ingest.
 *
 *   - Transfer rows (kind="transfer_order"): the date column shows ship-by while pending; once status is shipped or
 *     delivered it shows the actual shipped date from `public.transfer_order.shipped_at` (or the latest shipment row).
 *
 *   - Transfer request rows (kind="transfer_request"): open planner intent from mrp.transfer_request until converted
 *     to a pending transfer order on the Transfers workbench. Uses the same Transfer kind filter and lane display.
 *
 * "New PO" in the header links to the Purchase orders page. "New manual arrival"
 * opens a modal that writes to mrp.incoming_part (transfers, samples, and so on).
 *
 * The arrivals table includes a Supplier column (Megaventory name on open PO lines). Notes and
 * tracking numbers can be edited in place on every row kind.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { keepPreviousData, useMutation, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { ExternalLink, Pencil, Truck, X } from "lucide-react";

import { apiFetch, ApiError } from "@/lib/api";
import { HUB_CACHE_CHANNEL } from "@/lib/hubCacheChannels";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { useHubCacheStream } from "@/lib/useHubCacheStream";
import { MrpSkuDetailLink } from "@/components/MrpSkuDetailLink";
import { PurchaseOrderDetailLink } from "@/components/hub/HubEntityDetailLink";
import { MegaventoryPurchaseOrderExternalLink } from "@/components/mrp/MegaventoryPurchaseOrderLinks";
import { AssemblySkuCombobox } from "@/components/mrp/AssemblySkuCombobox";
import { DeferredCommitDateInput } from "@/components/mrp/DeferredCommitDateInput";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { SaveChangesButton } from "@/components/ui/SaveChangesButton";
import { Input, Label, TextArea } from "@/components/ui/Input";
import {
  HoverHintContent,
  HoverHintTrigger,
  HoverHintWithGlyph,
  ReadOnlyHoverLayer,
} from "@/components/ui/title-help";
import { cn } from "@/lib/utils";
import {
  computeHorizontalMenuPanel,
  FLOATING_POPOVER_Z_INDEX,
  type AnchoredMenuPanelBox,
} from "@/lib/viewportAnchoredPopover";
import {
  MRP_LOCATIONS,
  isMrpIncomingTransferKind,
  isMrpTransferOrderEditable,
  isMrpTransferRequestEditable,
  mrpLocationChipStyles,
  mrpTransferRequestStatusBadgeStyles,
  mrpTransferStatusBadgeStyles,
  type MrpIncomingFeedResponse,
  type MrpIncomingRow,
  type MrpLocation,
  type MrpTransferOrderStatus,
  type MrpTransferRequestStatus,
  formatMrpSkuHoverHintTitle,
  formatNumber,
  mrpIncomingCacheKey,
} from "@/lib/mrp";
import { isMrpIncomingRowOpen } from "@integrator-src/mrp/incoming-row-open";
import { TransferReasonSelect } from "@/components/mrp/TransferReasonSelect";
import { TransferEndpointLocationSelect } from "@/components/mrp/TransferEndpointLocationSelect";
import { useTransferEndpointLocations } from "@/lib/transferEndpointLocations";
import {
  locationFilterQueryParam,
  operationalLocationFilterOptions,
  parseLocationSearchParam,
} from "@/lib/operationalLocationFilter";

import {
  EMPTY_DRAFT,
  EMPTY_TRANSFER_DRAFT,
  type DraftNonPo,
  type DraftTransfer,
} from "./incomingPartsDefaults";
import { isMrpIncomingPartGhostSource } from "@integrator-src/mrp/incoming-part-ghost";
import { createHubPurchaseOrderAndNavigate } from "./purchaseOrderHubDraftNav";
import { inferCarrierFromTrackingNumber } from "@/lib/inferCarrierFromTracking";

type IncomingSortKey =
  | "cpn"
  | "supplier"
  | "location"
  | "rem_total"
  | "expected"
  | "detail"
  | "tracking"
  | "notes";

type IncomingRowMetadataVariables = {
  kind: MrpIncomingRow["kind"];
  id: number;
  delivery_schedule_id?: number | null;
  notes?: string | null;
  tracking_number?: string | null;
};

/** Megaventory web UI work order document — matches manufacturing hub links. */
function megaventoryWorkOrderDocumentUrl(mvId: number, workOrderNumber: string | null): string {
  const name = encodeURIComponent(`Build Order ${workOrderNumber ?? ""}`);
  return `https://smartgridsolutions.megaventory.com/inventory/default.aspx?document=WorkOrder&id=${mvId}&name=${name}`;
}

function formatIncomingMvStatusLabel(mvRaw: string | null | undefined): string {
  const t = mvRaw?.trim() ?? "";
  if (t === "") return "";
  const displayLabels: Record<string, string> = {
    PartiallyReceivedAndPartiallyInvoiced: "PARTIALLY RECEIVED",
  };
  const mapped = displayLabels[t];
  if (mapped != null) return mapped;
  return t.replace(/_/g, " ").toUpperCase();
}

function incomingPoNumberCell(row: MrpIncomingRow): ReactNode {
  const poNumberLabel =
    row.po_number ?? <span className="italic text-slate-400">no PO number</span>;
  const isHubOwned = String(row.system_of_record ?? "").trim().toLowerCase() === "hub";
  const showMegaventoryLink = row.purchase_order_mv_id != null && !isHubOwned;

  if (isHubOwned && row.purchase_order_id != null) {
    return (
      <PurchaseOrderDetailLink
        purchaseOrderId={row.purchase_order_id}
        purchaseOrderNumber={row.po_number}
        className="font-mono text-xs"
        peek={{
          purchase_order_number: row.po_number,
          promise_date: row.expected_arrival,
        }}
      />
    );
  }

  return (
    <>
      <span>{poNumberLabel}</span>
      {showMegaventoryLink ? (
        <MegaventoryPurchaseOrderExternalLink
          purchaseOrderMvId={row.purchase_order_mv_id!}
          poNumber={row.po_number}
        />
      ) : null}
    </>
  );
}

function poIncomingDetailStatusLabel(row: MrpIncomingRow): string {
  if (row.kind !== "po") return "";
  const fromHeader = formatIncomingMvStatusLabel(row.purchase_order_mv_status);
  if (fromHeader !== "") return fromHeader;
  if (row.status === "partial") return "PARTIAL RECEIPT";
  return "OPEN";
}

function workOrderIncomingDetailStatusLabel(row: MrpIncomingRow): string {
  if (row.kind !== "work_order") return "";
  const fromHeader = formatIncomingMvStatusLabel(row.work_order_mv_status);
  if (fromHeader !== "") return fromHeader;
  if (row.status === "partial") return "PARTIAL";
  return "OPEN";
}

function IncomingDocumentStatusBadge({ label }: { label: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded-md border border-slate-300 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-800",
      )}
    >
      {label}
    </span>
  );
}

function incomingKindToggleVisible(row: MrpIncomingRow, kindToggles: IncomingKindToggles): boolean {
  if (row.kind === "transfer_request") return kindToggles.transfer_order;
  return kindToggles[row.kind];
}

function sortValueDetail(row: MrpIncomingRow): string {
  if (row.kind === "non_po") return (row.source ?? "manual").toLowerCase();
  if (row.kind === "work_order") return (row.po_number ?? "work order").toLowerCase();
  if (row.kind === "transfer_request") return (row.po_number ?? "transfer request").toLowerCase();
  if (row.kind === "transfer_order") return (row.transfer_number ?? "transfer").toLowerCase();
  const gpo = (row.po_number ?? "").trim();
  if (row.allocated_stock != null && row.allocated_stock > 0) {
    return `${gpo}\tallocated ${row.allocated_stock}`;
  }
  return gpo || "—";
}

/**
 * Normalized expected-arrival for sorting and “has a schedule” checks.
 * Matches `expectedArrivalForDateInput`: null / empty / pre-1951 ISO sentinels are treated as missing
 * so they do not string-sort ahead of real dates while the date input shows blank.
 */
function incomingExpectedComparable(v: string | null | undefined): string | null {
  if (v == null || v === "") return null;
  const t = v.trim();
  if (t === "") return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(t)) {
    const day = t.slice(0, 10);
    const year = Number(day.slice(0, 4));
    if (!Number.isFinite(year) || year <= 1950) return null;
    return day;
  }
  return t;
}

/**
 * `dir` is applied after the primary key comparison: ascending means smaller values first
 * (missing expected dates are sorted last in ascending order).
 */
function compareIncoming(
  a: MrpIncomingRow,
  b: MrpIncomingRow,
  key: IncomingSortKey,
  dir: "asc" | "desc",
): number {
  const sign = dir === "asc" ? 1 : -1;
  let c = 0;
  if (key === "cpn") {
    c = a.cpn.localeCompare(b.cpn, undefined, { sensitivity: "base" });
  } else if (key === "supplier") {
    c = (a.supplier ?? "").localeCompare(b.supplier ?? "", undefined, { sensitivity: "base" });
  } else if (key === "location") {
    c = a.location_code.localeCompare(b.location_code);
  } else if (key === "rem_total") {
    c = a.quantity_remaining - b.quantity_remaining;
    if (c === 0) {
      const oa = a.quantity_ordered;
      const ob = b.quantity_ordered;
      if (oa == null && ob == null) c = 0;
      else if (oa == null) c = 1;
      else if (ob == null) c = -1;
      else c = oa - ob;
    }
  } else if (key === "expected") {
    const ea = incomingExpectedComparable(a.expected_arrival);
    const eb = incomingExpectedComparable(b.expected_arrival);
    if (ea == null && eb == null) c = 0;
    else if (ea == null) c = 1;
    else if (eb == null) c = -1;
    else c = ea < eb ? -1 : ea > eb ? 1 : 0;
  } else if (key === "detail") {
    c = sortValueDetail(a).localeCompare(sortValueDetail(b), undefined, { sensitivity: "base" });
  } else if (key === "tracking") {
    c = (a.tracking_number ?? "").localeCompare(b.tracking_number ?? "", undefined, {
      sensitivity: "base",
    });
  } else if (key === "notes") {
    c = (a.notes ?? "").localeCompare(b.notes ?? "", undefined, { sensitivity: "base" });
  }
  if (c === 0) c = a.cpn.localeCompare(b.cpn, undefined, { sensitivity: "base" });
  if (c === 0) c = a.kind === b.kind ? a.id - b.id : a.kind.localeCompare(b.kind);
  if (c === 0 && a.kind === "po" && b.kind === "po" && a.id === b.id) {
    const da = a.delivery_sort_order ?? -1;
    const db = b.delivery_sort_order ?? -1;
    c = da - db;
  }
  return c * sign;
}

function incomingRowKey(row: MrpIncomingRow): string {
  if (isMrpIncomingTransferKind(row.kind)) {
    return `${row.kind}-${row.transfer_group_id ?? row.id}-${row.transfer_role ?? "leg"}`;
  }
  return `${row.kind}-${row.id}-${row.delivery_schedule_id ?? "single"}`;
}

/**
 * Recompute table row order. While sorted by Expected, preserve the current order when only
 * row values change (for example an in-place date edit) so the row does not jump away from the
 * operator. Full resort when sort column/direction changes or when the filtered key set changes.
 */
function mergeIncomingRowOrder(
  previousOrder: string[],
  filteredRows: MrpIncomingRow[],
  sortKey: IncomingSortKey,
  sortDir: "asc" | "desc",
  sortChanged: boolean,
): string[] {
  const nextKeys = filteredRows.map(incomingRowKey);
  const nextKeySet = new Set(nextKeys);

  if (!sortChanged && sortKey === "expected" && previousOrder.length > 0) {
    const previousKeySet = new Set(previousOrder);
    const sameKeys =
      previousOrder.length === nextKeys.length && nextKeys.every((key) => previousKeySet.has(key));
    if (sameKeys) return previousOrder;

    const preserved = previousOrder.filter((key) => nextKeySet.has(key));
    const seen = new Set(preserved);
    for (const key of nextKeys) {
      if (!seen.has(key)) preserved.push(key);
    }
    return preserved;
  }

  const sorted = [...filteredRows];
  sorted.sort((a, b) => compareIncoming(a, b, sortKey, sortDir));
  return sorted.map(incomingRowKey);
}

/**
 * Set of `transfer_group_id` values where both the source (`from`) and the destination (`to`)
 * leg are present in the visible rows. The renderer hides the `from` leg of paired groups and
 * shows the `to` leg as a single grouped row with a "From → To" chip.
 */
function pairedTransferGroupIds(rows: MrpIncomingRow[]): Set<number> {
  const fromIds = new Set<number>();
  const toIds = new Set<number>();
  for (const row of rows) {
    if (!isMrpIncomingTransferKind(row.kind) || row.transfer_group_id == null) continue;
    if (row.transfer_role === "from") fromIds.add(row.transfer_group_id);
    else if (row.transfer_role === "to") toIds.add(row.transfer_group_id);
  }
  const paired = new Set<number>();
  for (const id of fromIds) {
    if (toIds.has(id)) paired.add(id);
  }
  return paired;
}

/** Which incoming row kinds are visible; each kind has its own toggle. */
type IncomingKindToggles = {
  po: boolean;
  non_po: boolean;
  work_order: boolean;
  transfer_order: boolean;
};

const DEFAULT_INCOMING_KIND_TOGGLES: IncomingKindToggles = {
  po: true,
  non_po: true,
  work_order: false,
  transfer_order: true,
};

function baseFilteredRows(
  rows: MrpIncomingRow[] | undefined,
  search: string,
  kindToggles: IncomingKindToggles,
  unscheduledOnly: boolean,
): MrpIncomingRow[] {
  const needle = search.trim().toLowerCase();
  let out: MrpIncomingRow[] = (rows ?? []).filter(isMrpIncomingRowOpen);
  out = out.filter((row) => incomingKindToggleVisible(row, kindToggles));
  if (unscheduledOnly) out = out.filter((row) => incomingExpectedComparable(row.expected_arrival) == null);
  if (needle) {
    out = out.filter((row) => {
      if (row.cpn.toLowerCase().includes(needle)) return true;
      if (row.po_number?.toLowerCase().includes(needle)) return true;
      if (row.supplier?.toLowerCase().includes(needle)) return true;
      if (row.source?.toLowerCase().includes(needle)) return true;
      if (row.notes?.toLowerCase().includes(needle)) return true;
      if (row.tracking_number?.toLowerCase().includes(needle)) return true;
      if (row.transfer_number?.toLowerCase().includes(needle)) return true;
      if (row.from_location_label?.toLowerCase().includes(needle)) return true;
      if (row.to_location_label?.toLowerCase().includes(needle)) return true;
      return false;
    });
  }
  return out;
}

function matchesArrivalDateTarget(
  row: MrpIncomingRow,
  id: number,
  kind: MrpIncomingRow["kind"],
  deliveryScheduleId: number | null,
): boolean {
  if (row.id !== id || row.kind !== kind) return false;
  if (kind === "po") {
    return (row.delivery_schedule_id ?? null) === (deliveryScheduleId ?? null);
  }
  return true;
}

function incomingMetadataPatchId(row: MrpIncomingRow): number {
  if (row.kind === "transfer_order" && row.transfer_order_id != null) {
    return row.transfer_order_id;
  }
  if (row.kind === "transfer_request") {
    return row.transfer_group_id ?? row.id;
  }
  return row.id;
}

function matchesIncomingMetadataTarget(
  row: MrpIncomingRow,
  variables: {
    kind: MrpIncomingRow["kind"];
    id: number;
    delivery_schedule_id?: number | null;
  },
): boolean {
  if (row.kind !== variables.kind) return false;
  if (incomingMetadataPatchId(row) !== variables.id) return false;
  if (variables.kind === "po") {
    return (row.delivery_schedule_id ?? null) === (variables.delivery_schedule_id ?? null);
  }
  return true;
}

/** API may send full ISO; `<input type="date">` needs YYYY-MM-DD. */
function expectedArrivalForDateInput(v: string | null | undefined): string {
  const comparable = incomingExpectedComparable(v);
  if (comparable != null) return comparable;
  if (v == null || v === "") return "";
  const t = v.trim();
  if (t === "") return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(t)) return "";
  return t;
}

function parseSegmentQuantityInput(s: string): number | null {
  const t = s.trim();
  if (t === "") return null;
  const n = Number.parseFloat(t.replace(/,/g, ""));
  if (!Number.isFinite(n)) return null;
  return n;
}

type IncomingPoDateContextMenu = {
  clientX: number;
  clientY: number;
  purchaseOrderItemId: number;
  poNumber: string | null;
  expectedDate: string | null;
};

const INCOMING_PARTS_PO_ONLY_KIND_TOGGLES: IncomingKindToggles = {
  po: true,
  non_po: false,
  work_order: false,
  transfer_order: false,
};

export function IncomingParts() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const hubWritesAllowed = useHubModuleWrite("mrp");
  const mrpReadOnlyTitle = "Your role has read-only access to the MRP hub module.";
  const [locationFilter, setLocationFilter] = useState<Set<string>>(() => new Set());
  const [search, setSearch] = useState("");
  const [kindToggles, setKindToggles] = useState<IncomingKindToggles>(() => ({
    ...DEFAULT_INCOMING_KIND_TOGGLES,
  }));
  const [draft, setDraft] = useState<DraftNonPo>(EMPTY_DRAFT);
  const [manualArrivalModalOpen, setManualArrivalModalOpen] = useState(false);
  const [transferDraft, setTransferDraft] = useState<DraftTransfer>(EMPTY_TRANSFER_DRAFT);
  const [showTransferForm, setShowTransferForm] = useState(false);
  const transferLocationsQuery = useTransferEndpointLocations();
  const locationOptions = useMemo(
    () => operationalLocationFilterOptions(transferLocationsQuery.locations),
    [transferLocationsQuery.locations],
  );
  const availableLocationCodes = useMemo(
    () => locationOptions.map((option) => option.value),
    [locationOptions],
  );

  const incomingLocationParam = useMemo(
    () => locationFilterQueryParam(locationFilter, availableLocationCodes),
    [locationFilter, availableLocationCodes],
  );
  const locationCodesFromUrl = useMemo(
    () => parseLocationSearchParam(searchParams.get("location")),
    [searchParams],
  );

  useEffect(() => {
    if (availableLocationCodes.length === 0) return;
    setLocationFilter((current) => {
      if (locationCodesFromUrl.length > 0) {
        const valid = locationCodesFromUrl.filter((code) => availableLocationCodes.includes(code));
        if (valid.length > 0) {
          const same = current.size === valid.length && valid.every((code) => current.has(code));
          return same ? current : new Set(valid);
        }
      }
      if (current.size === 0) return new Set(availableLocationCodes);
      return current;
    });
  }, [availableLocationCodes, locationCodesFromUrl]);
  const incomingQueryKey = ["mrp", "incoming", { locations: incomingLocationParam ?? "" }] as const;
  const incomingHubCacheChannel = HUB_CACHE_CHANNEL.mrpIncoming(
    mrpIncomingCacheKey({ locations: incomingLocationParam ?? "" }),
  );

  const incomingQuery = useQuery({
    queryKey: incomingQueryKey,
    queryFn: () =>
      apiFetch<MrpIncomingFeedResponse>("/api/mrp/incoming", {
        query: { location: incomingLocationParam, background: "1" },
      }),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    placeholderData: keepPreviousData,
  });

  useHubCacheStream({
    channels: [incomingHubCacheChannel],
    onEvent: (event) => {
      if (event.phase === "ready" || event.phase === "invalidated") {
        void queryClient.invalidateQueries({ queryKey: incomingQueryKey });
      }
    },
  });

  const skuHoverHints = incomingQuery.data?.sku_hover_hints ?? {};
  const [unscheduledOnly, setUnscheduledOnly] = useState(false);
  const [sortKey, setSortKey] = useState<IncomingSortKey>("expected");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [rowOrder, setRowOrder] = useState<string[]>([]);
  const incomingSortRef = useRef({ sortKey, sortDir });
  const [splitDialog, setSplitDialog] = useState<{
    purchaseOrderItemId: number;
    cpn: string;
    prefillExpectedArrival: string | null;
  } | null>(null);
  const [poDateContextMenu, setPoDateContextMenu] = useState<IncomingPoDateContextMenu | null>(null);

  useEffect(() => {
    const po = searchParams.get("po")?.trim();
    const cpn = searchParams.get("cpn")?.trim();
    if (cpn) {
      setSearch(cpn);
      return;
    }
    if (po == null || po === "") return;
    setSearch(po);
    setKindToggles(INCOMING_PARTS_PO_ONLY_KIND_TOGGLES);
  }, [searchParams]);

  const baseFiltered = useMemo(
    () => baseFilteredRows(incomingQuery.data?.rows, search, kindToggles, unscheduledOnly),
    [incomingQuery.data, search, kindToggles, unscheduledOnly],
  );

  useLayoutEffect(() => {
    const sortChanged =
      incomingSortRef.current.sortKey !== sortKey || incomingSortRef.current.sortDir !== sortDir;
    incomingSortRef.current = { sortKey, sortDir };
    setRowOrder((previousOrder) =>
      mergeIncomingRowOrder(previousOrder, baseFiltered, sortKey, sortDir, sortChanged),
    );
  }, [baseFiltered, sortKey, sortDir]);

  const pairedGroupIds = useMemo(() => pairedTransferGroupIds(baseFiltered), [baseFiltered]);

  const displayedRows = useMemo(() => {
    const byKey = new Map(baseFiltered.map((row) => [incomingRowKey(row), row]));
    const ordered: MrpIncomingRow[] = [];
    if (rowOrder.length === 0) {
      ordered.push(...baseFiltered);
      ordered.sort((a, b) => compareIncoming(a, b, sortKey, sortDir));
    } else {
      for (const key of rowOrder) {
        const row = byKey.get(key);
        if (row) ordered.push(row);
      }
      const seen = new Set(rowOrder);
      for (const row of baseFiltered) {
        const key = incomingRowKey(row);
        if (!seen.has(key)) ordered.push(row);
      }
    }
    // For paired transfer groups (both legs visible) hide the source (negative) leg and
    // keep the destination leg as the single grouped row.
    return ordered.filter((row) => {
      if (!isMrpIncomingTransferKind(row.kind)) return true;
      if (row.transfer_group_id == null) return true;
      if (!pairedGroupIds.has(row.transfer_group_id)) return true;
      return row.transfer_role === "to";
    });
  }, [baseFiltered, rowOrder, sortKey, sortDir, pairedGroupIds]);

  const onSort = (key: IncomingSortKey): void => {
    if (key === sortKey) {
      setSortDir((direction) => (direction === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const unscheduledCount = useMemo(
    () =>
      (incomingQuery.data?.rows ?? [])
        .filter(isMrpIncomingRowOpen)
        .filter((row) => incomingExpectedComparable(row.expected_arrival) == null).length,
    [incomingQuery.data],
  );

  const counts = useMemo(() => {
    const rows = (incomingQuery.data?.rows ?? []).filter(isMrpIncomingRowOpen);
    const transferGroups = new Set<number>();
    for (const row of rows) {
      if (isMrpIncomingTransferKind(row.kind) && row.transfer_group_id != null) {
        transferGroups.add(row.transfer_group_id);
      }
    }
    return {
      po: rows.filter((row) => row.kind === "po").length,
      non_po: rows.filter((row) => row.kind === "non_po").length,
      work_order: rows.filter((row) => row.kind === "work_order").length,
      transfer_order: transferGroups.size,
    };
  }, [incomingQuery.data]);

  const invalidateAll = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["mrp", "incoming"] });
    void queryClient.invalidateQueries({ queryKey: ["mrp", "purchase-orders"] });
    void queryClient.invalidateQueries({ queryKey: ["mrp", "incoming-parts"] });
    void queryClient.invalidateQueries({ queryKey: ["mrp", "forecast"] });
  };

  const createNonPoMutation = useMutation({
    mutationFn: (input: DraftNonPo) =>
      apiFetch("/api/mrp/incoming-parts", {
        method: "POST",
        body: {
          cpn: input.cpn,
          location_code: input.location_code,
          quantity: Number.parseInt(input.quantity || "0", 10) || 0,
          expected_arrival: input.expected_arrival || null,
          source: input.source || null,
          notes: input.notes || null,
          tracking_number: input.tracking_number.trim() === "" ? null : input.tracking_number.trim(),
          tracking_carrier: input.tracking_carrier.trim() === "" ? null : input.tracking_carrier.trim(),
        },
      }),
    onSuccess: () => {
      setDraft(EMPTY_DRAFT);
      setManualArrivalModalOpen(false);
      invalidateAll();
    },
  });

  const closeManualArrivalModal = (): void => {
    createNonPoMutation.reset();
    setDraft(EMPTY_DRAFT);
    setManualArrivalModalOpen(false);
  };

  useEffect(() => {
    if (!manualArrivalModalOpen) return;
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") closeManualArrivalModal();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [manualArrivalModalOpen, createNonPoMutation]);

  useEffect(() => {
    if (poDateContextMenu == null) return;
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") setPoDateContextMenu(null);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [poDateContextMenu]);

  const updateArrivalDateMutation = useMutation({
    mutationFn: ({ id, kind, value, deliveryScheduleId }) => {
      if (kind === "po") {
        if (deliveryScheduleId != null) {
          return apiFetch(`/api/mrp/mv-purchase-order-items/${id}/deliveries/${deliveryScheduleId}`, {
            method: "PATCH",
            body: { expected_arrival: value },
          });
        }
        return apiFetch(`/api/mrp/mv-purchase-order-items/${id}`, {
          method: "PATCH",
          body: { expected_date: value },
        });
      }
      if (kind === "work_order") {
        return apiFetch<{ ok: boolean }>(`/api/mrp/build-orders/${id}/incoming-expected-arrival`, {
          method: "PATCH",
          body: { expected_arrival: value },
        });
      }
      if (kind === "transfer_order") {
        return Promise.reject(new Error("Transfer ship-by date is edited via the New transfer dialog."));
      }
      if (kind === "transfer_request") {
        return apiFetch(`/api/mrp/transfer-requests/${id}`, {
          method: "PATCH",
          body: { target_date: value },
        });
      }
      return apiFetch(`/api/mrp/incoming-parts/${id}`, {
        method: "PATCH",
        body: { expected_arrival: value },
      });
    },
    onMutate: async (variables: {
      id: number;
      kind: MrpIncomingRow["kind"];
      value: string | null;
      deliveryScheduleId: number | null;
    }) => {
      await queryClient.cancelQueries({ queryKey: ["mrp", "incoming"] });
      const snapshots = queryClient.getQueriesData({ queryKey: ["mrp", "incoming"] });
      queryClient.setQueriesData({ queryKey: ["mrp", "incoming"] }, (old) => {
        if (old == null || typeof old !== "object" || !("rows" in old)) return old;
        const record = old as { rows: MrpIncomingRow[] };
        return {
          ...record,
          rows: record.rows.map((row) =>
            matchesArrivalDateTarget(row, variables.id, variables.kind, variables.deliveryScheduleId)
              ? { ...row, expected_arrival: variables.value }
              : row,
          ),
        };
      });
      return { snapshots };
    },
    onError: (
      _err: unknown,
      _variables: { id: number; kind: MrpIncomingRow["kind"]; value: string | null; deliveryScheduleId: number | null },
      context: { snapshots: [QueryKey, unknown][] } | undefined,
    ) => {
      for (const [key, data] of context?.snapshots ?? []) {
        queryClient.setQueryData(key, data);
      }
    },
    onSuccess: (
      _data: unknown,
      variables: { id: number; kind: MrpIncomingRow["kind"]; value: string | null; deliveryScheduleId: number | null },
    ) => {
      if (variables.kind === "po" && variables.deliveryScheduleId == null) {
        void queryClient.invalidateQueries({ queryKey: ["mrp", "incoming"] });
      }
      if (variables.kind === "work_order" || variables.kind === "transfer_request") {
        void queryClient.invalidateQueries({ queryKey: ["mrp", "incoming"] });
      }
      void queryClient.invalidateQueries({ queryKey: ["mrp", "purchase-orders"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "incoming-parts"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "forecast"] });
    },
  });

  const setPurchaseOrderExpectedDateMutation = useMutation({
    mutationFn: (input: { purchaseOrderItemId: number; expectedDate: string | null }) =>
      apiFetch<{ updated_line_count: number }>(
        `/api/mrp/mv-purchase-order-items/${input.purchaseOrderItemId}/expected-date-for-purchase-order`,
        {
          method: "PATCH",
          body: { expected_date: input.expectedDate },
        },
      ),
    onSuccess: () => {
      setPoDateContextMenu(null);
      invalidateAll();
    },
  });

  const updateIncomingRowMetadataMutation = useMutation({
    mutationFn: (input: IncomingRowMetadataVariables) =>
      apiFetch("/api/mrp/incoming-row-metadata", {
        method: "PATCH",
        body: input,
      }),
    onMutate: async (variables: IncomingRowMetadataVariables) => {
      await queryClient.cancelQueries({ queryKey: ["mrp", "incoming"] });
      const snapshots = queryClient.getQueriesData({ queryKey: ["mrp", "incoming"] });
      queryClient.setQueriesData({ queryKey: ["mrp", "incoming"] }, (old) => {
        if (old == null || typeof old !== "object" || !("rows" in old)) return old;
        const record = old as { rows: MrpIncomingRow[] };
        return {
          ...record,
          rows: record.rows.map((row) => {
            if (!matchesIncomingMetadataTarget(row, variables)) return row;
            return {
              ...row,
              ...(variables.notes !== undefined ? { notes: variables.notes } : {}),
              ...(variables.tracking_number !== undefined
                ? { tracking_number: variables.tracking_number }
                : {}),
            };
          }),
        };
      });
      return { snapshots };
    },
    onError: (
      _err: unknown,
      _variables: IncomingRowMetadataVariables,
      context: { snapshots: [QueryKey, unknown][] } | undefined,
    ) => {
      for (const [key, data] of context?.snapshots ?? []) {
        queryClient.setQueryData(key, data);
      }
    },
    onSuccess: invalidateAll,
  });

  const saveIncomingNotes = (
    row: MrpIncomingRow,
    notes: string | null,
  ): void => {
    updateIncomingRowMetadataMutation.mutate({
      kind: row.kind,
      id: incomingMetadataPatchId(row),
      delivery_schedule_id: row.kind === "po" ? row.delivery_schedule_id : undefined,
      notes,
    });
  };

  const saveIncomingTracking = (
    row: MrpIncomingRow,
    tracking_number: string | null,
  ): void => {
    updateIncomingRowMetadataMutation.mutate({
      kind: row.kind,
      id: incomingMetadataPatchId(row),
      delivery_schedule_id: row.kind === "po" ? row.delivery_schedule_id : undefined,
      tracking_number,
    });
  };

  const saveDeliveriesMutation = useMutation({
    mutationFn: (input: { purchaseOrderItemId: number; segments: { quantity: number; expected_arrival: string | null }[] }) =>
      apiFetch<{ ok: boolean }>(`/api/mrp/mv-purchase-order-items/${input.purchaseOrderItemId}/deliveries`, {
        method: "PUT",
        body: { segments: input.segments },
      }),
    onSuccess: () => {
      setSplitDialog(null);
      invalidateAll();
    },
  });

  const deleteNonPoMutation = useMutation({
    mutationFn: (id: number) => apiFetch(`/api/mrp/incoming-parts/${id}`, { method: "DELETE" }),
    onSuccess: invalidateAll,
  });

  const createTransferMutation = useMutation({
    mutationFn: (input: DraftTransfer) =>
      apiFetch("/api/mrp/transfer-orders", {
        method: "POST",
        body: {
          cpn: input.cpn.trim(),
          quantity: Number.parseInt(input.quantity || "0", 10) || 0,
          origin_location_id: Number.parseInt(input.origin_location_id, 10),
          destination_location_id: Number.parseInt(input.destination_location_id, 10),
          ship_by_date: input.ship_by_date,
          reason: input.reason,
          notes: input.notes.trim() || null,
        },
      }),
    onSuccess: () => {
      setTransferDraft(EMPTY_TRANSFER_DRAFT);
      setShowTransferForm(false);
      invalidateAll();
    },
  });

  const deleteTransferMutation = useMutation({
    mutationFn: (transferOrderId: number) =>
      apiFetch(`/api/mrp/transfer-orders/${transferOrderId}`, { method: "DELETE" }),
    onSuccess: invalidateAll,
  });

  const toggleKind = (key: keyof IncomingKindToggles): void => {
    setKindToggles((current) => ({ ...current, [key]: !current[key] }));
  };

  const toggleLocation = (code: string): void => {
    setLocationFilter((current) => {
      const next = new Set(current);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-semibold text-slate-900">Incoming parts</h1>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            onClick={() => {
              void createHubPurchaseOrderAndNavigate(navigate, { location_code: "sgs" });
            }}
          >
            New PO
          </Button>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => {
              createNonPoMutation.reset();
              setDraft(EMPTY_DRAFT);
              setManualArrivalModalOpen(true);
            }}
          >
            New manual arrival
          </Button>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => {
              setShowTransferForm((value) => !value);
            }}
          >
            {showTransferForm ? "Hide transfer form" : "New transfer"}
          </Button>
        </div>
      </header>

      {incomingQuery.isError ? (
        <Card>
          <CardBody>
            <p className="text-sm font-medium text-rose-800">Incoming list could not be loaded</p>
            <p className="mt-1 text-sm text-rose-700">{(incomingQuery.error as Error).message}</p>
            {(() => {
              const err = incomingQuery.error;
              const looksLikeServerBug =
                (err instanceof ApiError && err.status >= 500) ||
                (err instanceof Error && /internal server error/i.test(err.message));
              if (looksLikeServerBug) {
                return (
                  <p className="mt-2 text-xs text-slate-600">
                    A response in the 500 range usually means the API hit a database error (for example a column added
                    in the code but not in the database yet). From the project root, run{" "}
                    <code className="rounded bg-slate-100 px-1">npm run migrate</code>, restart the API, then reload
                    this page.
                  </p>
                );
              }
              return (
                <p className="mt-2 text-xs text-slate-600">
                  If the message mentions authorization or a token, sign in again. This page needs an active session
                  and org admin (org 1, role 1) on the same Supabase project the API is configured for. The spreadsheet
                  import writes to the database given by the root{" "}
                  <code className="rounded bg-slate-100 px-1">.env</code> <code className="rounded bg-slate-100 px-1">DATABASE_URL</code>{" "}
                  — the same value must be used for the integrator API server that serves this UI.
                </p>
              );
            })()}
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Kind</span>
            {(
              [
                { key: "po" as const, label: "PO", count: counts.po },
                { key: "non_po" as const, label: "Manual", count: counts.non_po },
                { key: "work_order" as const, label: "Work order", count: counts.work_order },
                { key: "transfer_order" as const, label: "Transfer", count: counts.transfer_order },
              ] as const
            ).map((option) => {
              const isOn = kindToggles[option.key];
              return (
                <Button
                  key={option.key}
                  size="sm"
                  variant={isOn ? "primary" : "secondary"}
                  onClick={() => toggleKind(option.key)}
                >
                  {option.label} ({option.count})
                </Button>
              );
            })}
          </div>
          <div className="mt-3 flex flex-col gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Locations</span>
            <div className="flex flex-wrap gap-2">
              {locationOptions.map((option) => {
                const isOn = locationFilter.has(option.value);
                return (
                  <Button
                    key={option.value}
                    size="sm"
                    variant={isOn ? "primary" : "secondary"}
                    onClick={() => toggleLocation(option.value)}
                    disabled={transferLocationsQuery.isLoading}
                  >
                    {option.label}
                  </Button>
                );
              })}
            </div>
          </div>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Schedule</span>
              <Button
                size="sm"
                variant={unscheduledOnly ? "primary" : "secondary"}
                onClick={() => setUnscheduledOnly((value) => !value)}
              >
                No expected date ({unscheduledCount})
              </Button>
            </div>
            <div>
              <Label htmlFor="incoming-text-filter" className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
                Search
              </Label>
              <Input
                id="incoming-text-filter"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="CPN, PO #, supplier, or source…"
              />
            </div>
          </div>
        </CardBody>
      </Card>

      {showTransferForm ? (
        <Card>
          <CardHeader>
            <CardTitle>New transfer</CardTitle>
          </CardHeader>
          <CardBody>
            <p className="mb-3 text-xs text-slate-500">
              Creates a <code>public.transfer_order</code> in <span className="font-mono">pending</span> status. It
              shows up here as a single grouped row (source and destination legs) until the transfer-order app
              promotes it through <span className="font-mono">packing</span>, <span className="font-mono">shipped</span>,
              and finally <span className="font-mono">received</span>. Edit and delete are only available while
              <span className="font-mono"> pending</span>.
            </p>
            <form
              className="grid gap-3 md:grid-cols-3"
              onSubmit={(event) => {
                event.preventDefault();
                if (!hubWritesAllowed) return;
                if (
                  !transferDraft.cpn.trim() ||
                  !transferDraft.quantity ||
                  !transferDraft.origin_location_id ||
                  !transferDraft.destination_location_id ||
                  !transferDraft.ship_by_date ||
                  transferDraft.origin_location_id === transferDraft.destination_location_id
                ) {
                  return;
                }
                createTransferMutation.mutate(transferDraft);
              }}
            >
              <div>
                <Label>CPN</Label>
                <Input
                  value={transferDraft.cpn}
                  disabled={!hubWritesAllowed}
                  onChange={(event) =>
                    setTransferDraft((prev) => ({ ...prev, cpn: event.target.value }))
                  }
                />
              </div>
              <div>
                <Label>Quantity</Label>
                <Input
                  type="number"
                  min={1}
                  value={transferDraft.quantity}
                  disabled={!hubWritesAllowed}
                  onChange={(event) =>
                    setTransferDraft((prev) => ({ ...prev, quantity: event.target.value }))
                  }
                />
              </div>
              <div>
                <Label>Ship by date</Label>
                <Input
                  type="date"
                  value={transferDraft.ship_by_date}
                  disabled={!hubWritesAllowed}
                  onChange={(event) =>
                    setTransferDraft((prev) => ({ ...prev, ship_by_date: event.target.value }))
                  }
                />
              </div>
              <div>
                <Label>Origin</Label>
                <TransferEndpointLocationSelect
                  locations={transferLocationsQuery.locations}
                  value={transferDraft.origin_location_id}
                  disabled={!hubWritesAllowed}
                  emptyLabel="Select origin…"
                  onChange={(next) =>
                    setTransferDraft((prev) => ({ ...prev, origin_location_id: next }))
                  }
                />
              </div>
              <div>
                <Label>Destination</Label>
                <TransferEndpointLocationSelect
                  locations={transferLocationsQuery.locations}
                  value={transferDraft.destination_location_id}
                  disabled={!hubWritesAllowed}
                  emptyLabel="Select destination…"
                  onChange={(next) =>
                    setTransferDraft((prev) => ({ ...prev, destination_location_id: next }))
                  }
                />
              </div>
              <div>
                <TransferReasonSelect
                  label="Reason"
                  value={transferDraft.reason}
                  disabled={!hubWritesAllowed}
                  onChange={(reason) =>
                    setTransferDraft((prev) => ({
                      ...prev,
                      reason,
                    }))
                  }
                />
              </div>
              <div className="md:col-span-3">
                <Label>Notes</Label>
                <Input
                  value={transferDraft.notes}
                  disabled={!hubWritesAllowed}
                  onChange={(event) =>
                    setTransferDraft((prev) => ({ ...prev, notes: event.target.value }))
                  }
                />
              </div>
              {transferDraft.origin_location_id &&
              transferDraft.destination_location_id &&
              transferDraft.origin_location_id === transferDraft.destination_location_id ? (
                <p className="md:col-span-3 text-sm text-rose-700">
                  Origin and destination must differ.
                </p>
              ) : null}
              <div className="md:col-span-3 flex justify-end">
                <Button
                  type="submit"
                  size="sm"
                  isLoading={createTransferMutation.isPending}
                  disabled={
                    !hubWritesAllowed ||
                    !transferDraft.cpn.trim() ||
                    !transferDraft.quantity ||
                    !transferDraft.origin_location_id ||
                    !transferDraft.destination_location_id ||
                    !transferDraft.ship_by_date ||
                    transferDraft.origin_location_id === transferDraft.destination_location_id
                  }
                >
                  Create transfer
                </Button>
              </div>
              {createTransferMutation.error ? (
                <p className="md:col-span-3 text-sm text-rose-700">
                  {(createTransferMutation.error as Error).message}
                </p>
              ) : null}
            </form>
          </CardBody>
        </Card>
      ) : null}

      {locationFilter.size === 0 ? (
        <Card>
          <CardBody>
            <p className="text-sm text-slate-600">Toggle on at least one location to see incoming parts.</p>
          </CardBody>
        </Card>
      ) : (
      <Card>
        <CardBody className="p-0">
          <div className="max-h-[calc(100vh-12rem)] overflow-auto">
          <table className="w-full min-w-[65rem] table-fixed border-collapse text-sm">
            <colgroup>
              <col className="w-[7.5rem]" />
              <col className="w-[7.5rem]" />
              <col className="w-[6.75rem]" />
              <col className="w-[6.75rem]" />
              <col className="w-[9.25rem]" />
              <col className="w-[10.5rem]" />
              <col className="w-[2.75rem]" />
              <col />
              <col className="w-[7.75rem]" />
            </colgroup>
            <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50/95 text-left text-xs uppercase tracking-wide text-slate-500 backdrop-blur-sm">
              <tr>
                <IncomingSortTh column="cpn" sortKey={sortKey} sortDir={sortDir} onSort={onSort}>
                  CPN
                </IncomingSortTh>
                <IncomingSortTh column="supplier" sortKey={sortKey} sortDir={sortDir} onSort={onSort}>
                  Supplier
                </IncomingSortTh>
                <IncomingSortTh column="location" sortKey={sortKey} sortDir={sortDir} onSort={onSort}>
                  Location
                </IncomingSortTh>
                <IncomingSortTh
                  column="rem_total"
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSort={onSort}
                  align="right"
                  sortHint="Remaining quantity / ordered quantity"
                  className="whitespace-nowrap"
                >
                  Rem / total
                </IncomingSortTh>
                <IncomingSortTh column="expected" sortKey={sortKey} sortDir={sortDir} onSort={onSort}>
                  Expected
                </IncomingSortTh>
                <IncomingSortTh column="detail" sortKey={sortKey} sortDir={sortDir} onSort={onSort}>
                  Detail
                </IncomingSortTh>
                <IncomingSortTh
                  column="tracking"
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSort={onSort}
                  align="center"
                  className="w-0 whitespace-nowrap px-2"
                >
                  Tracking
                </IncomingSortTh>
                <IncomingSortTh column="notes" sortKey={sortKey} sortDir={sortDir} onSort={onSort} className="min-w-[10rem]">
                  Notes
                </IncomingSortTh>
                <th className="w-[7.75rem] min-w-[7.75rem] whitespace-nowrap px-3 py-2 text-right align-top">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {incomingQuery.isPending ? (
                <tr>
                  <td className="px-4 py-6 text-center text-slate-500" colSpan={9}>
                    Loading…
                  </td>
                </tr>
              ) : null}
              {!incomingQuery.isPending
                ? displayedRows.map((row) => {
                    const isPairedTransfer =
                      isMrpIncomingTransferKind(row.kind) &&
                      row.transfer_group_id != null &&
                      pairedGroupIds.has(row.transfer_group_id);
                    return (
                      <IncomingRow
                        key={incomingRowKey(row)}
                        row={row}
                        hubWritesAllowed={hubWritesAllowed}
                        hubReadOnlyTitle={mrpReadOnlyTitle}
                        isPairedTransfer={isPairedTransfer}
                        skuHoverHintTitle={formatMrpSkuHoverHintTitle(
                          row.cpn,
                          skuHoverHints[row.cpn],
                        )}
                        onDeleteNonPo={() => {
                          if (window.confirm(`Delete manual arrival for ${row.cpn}?`)) {
                            deleteNonPoMutation.mutate(row.id);
                          }
                        }}
                        onDeleteTransfer={() => {
                          if (
                            row.transfer_order_id != null &&
                            window.confirm(
                              `Delete pending transfer ${row.transfer_number ?? ""} for ${row.cpn}?`,
                            )
                          ) {
                            deleteTransferMutation.mutate(row.transfer_order_id);
                          }
                        }}
                        deleteTransferPending={
                          deleteTransferMutation.isPending &&
                          deleteTransferMutation.variables === row.transfer_order_id
                        }
                        onUpdateDate={(value) => {
                          updateArrivalDateMutation.mutate({
                            id: row.id,
                            kind: row.kind,
                            value,
                            deliveryScheduleId: row.kind === "po" ? row.delivery_schedule_id : null,
                          });
                        }}
                        onOpenSplitDeliveries={
                          hubWritesAllowed && row.kind === "po"
                            ? () =>
                                setSplitDialog({
                                  purchaseOrderItemId: row.id,
                                  cpn: row.cpn,
                                  prefillExpectedArrival: row.expected_arrival,
                                })
                            : undefined
                        }
                        datePending={
                          updateArrivalDateMutation.isPending &&
                          updateArrivalDateMutation.variables?.id === row.id &&
                          updateArrivalDateMutation.variables?.kind === row.kind &&
                          updateArrivalDateMutation.variables?.deliveryScheduleId ===
                            (row.kind === "po" ? row.delivery_schedule_id : null)
                        }
                        dateCommitError={
                          updateArrivalDateMutation.isError &&
                          updateArrivalDateMutation.variables?.id === row.id &&
                          updateArrivalDateMutation.variables?.kind === row.kind &&
                          updateArrivalDateMutation.variables?.deliveryScheduleId ===
                            (row.kind === "po" ? row.delivery_schedule_id : null)
                            ? (updateArrivalDateMutation.error as Error).message
                            : null
                        }
                        onSaveNotes={
                          hubWritesAllowed && row.kind !== "transfer_request"
                            ? (notes) => saveIncomingNotes(row, notes)
                            : undefined
                        }
                        onSaveTracking={
                          hubWritesAllowed && row.kind !== "transfer_request"
                            ? (tracking_number) => saveIncomingTracking(row, tracking_number)
                            : undefined
                        }
                        metadataSavePending={updateIncomingRowMetadataMutation.isPending}
                        metadataSaveVariables={updateIncomingRowMetadataMutation.variables}
                        onDateContextMenu={
                          hubWritesAllowed && row.kind === "po"
                            ? (event, draft) => {
                                event.preventDefault();
                                setPoDateContextMenu({
                                  clientX: event.clientX,
                                  clientY: event.clientY,
                                  purchaseOrderItemId: row.id,
                                  poNumber: row.po_number,
                                  expectedDate: draft.trim() === "" ? null : draft.trim(),
                                });
                              }
                            : undefined
                        }
                      />
                    );
                  })
                : null}
              {!incomingQuery.isPending && !incomingQuery.isError && displayedRows.length === 0 ? (
                <tr>
                  <td className="px-4 py-6 text-center text-slate-500" colSpan={9}>
                    Nothing arriving in this slice. Refresh after Megaventory or imports update open quantities.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
          </div>
        </CardBody>
      </Card>
      )}

      {manualArrivalModalOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          role="presentation"
          onClick={closeManualArrivalModal}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="manual-arrival-title"
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-slate-200 bg-white p-5 shadow-lg"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex flex-wrap items-center gap-2">
              <h2 id="manual-arrival-title" className="text-lg font-semibold text-slate-900">
                Add manual arrival
              </h2>
              <HoverHintWithGlyph hoverHint="Use for non-PO inbound: transfers, samples, general supplies, or one-off replacements. Set Source to the vendor or shipper name so Shipping Arrivals can show it on the expected queue. Put handoff instructions (for example give to a person) in Notes. Tracking and carrier pre-fill the dock arrival when freight lands. For a real purchase order, use New PO. Close with Escape or click outside the form." />
            </div>
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={mrpReadOnlyTitle} layout="block">
              <form
                className="mt-4 space-y-3"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (!hubWritesAllowed) return;
                  if (!draft.cpn.trim() || !draft.quantity) return;
                  createNonPoMutation.mutate(draft);
                }}
              >
                <div>
                  <Label htmlFor="manual-arrival-cpn">CPN</Label>
                  <AssemblySkuCombobox
                    id="manual-arrival-cpn"
                    value={draft.cpn}
                    disabled={!hubWritesAllowed}
                    onChange={(next) => setDraft((prev) => ({ ...prev, cpn: next }))}
                    placeholder="Search or type CPN…"
                    className="w-full"
                  />
                </div>
                <div>
                  <Label htmlFor="manual-arrival-location">Location</Label>
                  <select
                    id="manual-arrival-location"
                    value={draft.location_code}
                    disabled={!hubWritesAllowed}
                    onChange={(event) =>
                      setDraft((prev) => ({ ...prev, location_code: event.target.value as MrpLocation }))
                    }
                    className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm disabled:opacity-60"
                  >
                    {MRP_LOCATIONS.map((location) => (
                      <option key={location.code} value={location.code}>
                        {location.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="manual-arrival-quantity">Quantity</Label>
                  <Input
                    id="manual-arrival-quantity"
                    type="number"
                    value={draft.quantity}
                    disabled={!hubWritesAllowed}
                    onChange={(event) => setDraft((prev) => ({ ...prev, quantity: event.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="manual-arrival-expected">Expected arrival</Label>
                  <Input
                    id="manual-arrival-expected"
                    type="date"
                    value={draft.expected_arrival}
                    disabled={!hubWritesAllowed}
                    onChange={(event) => setDraft((prev) => ({ ...prev, expected_arrival: event.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="manual-arrival-source">Source</Label>
                  <Input
                    id="manual-arrival-source"
                    value={draft.source}
                    disabled={!hubWritesAllowed}
                    onChange={(event) => setDraft((prev) => ({ ...prev, source: event.target.value }))}
                    placeholder="Vendor, Amazon, internal transfer…"
                  />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="manual-arrival-tracking">Tracking number</Label>
                    <Input
                      id="manual-arrival-tracking"
                      value={draft.tracking_number}
                      disabled={!hubWritesAllowed}
                      onChange={(event) =>
                        setDraft((prev) => ({ ...prev, tracking_number: event.target.value }))
                      }
                      onBlur={() => {
                        if (draft.tracking_carrier.trim() !== "") return;
                        const inferred = inferCarrierFromTrackingNumber(draft.tracking_number);
                        if (inferred != null) {
                          setDraft((prev) => ({ ...prev, tracking_carrier: inferred }));
                        }
                      }}
                      className="font-mono"
                      placeholder="Carrier tracking number"
                    />
                  </div>
                  <div>
                    <Label htmlFor="manual-arrival-carrier">Carrier</Label>
                    <Input
                      id="manual-arrival-carrier"
                      value={draft.tracking_carrier}
                      disabled={!hubWritesAllowed}
                      onChange={(event) =>
                        setDraft((prev) => ({ ...prev, tracking_carrier: event.target.value }))
                      }
                      placeholder="Auto-filled from tracking when recognized"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="manual-arrival-notes">Notes</Label>
                  <Input
                    id="manual-arrival-notes"
                    value={draft.notes}
                    disabled={!hubWritesAllowed}
                    onChange={(event) => setDraft((prev) => ({ ...prev, notes: event.target.value }))}
                    placeholder="Handoff instructions, job reference…"
                  />
                </div>
                <div className="flex flex-wrap gap-2 pt-2">
                  <Button
                    type="submit"
                    size="sm"
                    variant="primary"
                    isLoading={createNonPoMutation.isPending}
                    disabled={!hubWritesAllowed || !draft.cpn.trim() || !draft.quantity}
                  >
                    Add manual arrival
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={closeManualArrivalModal}>
                    Cancel
                  </Button>
                </div>
                {createNonPoMutation.error ? (
                  <p className="text-sm text-rose-700">{(createNonPoMutation.error as Error).message}</p>
                ) : null}
              </form>
            </ReadOnlyHoverLayer>
          </div>
        </div>
      ) : null}

      {splitDialog ? (
        <SplitDeliveriesDialog
          purchaseOrderItemId={splitDialog.purchaseOrderItemId}
          cpn={splitDialog.cpn}
          prefillExpectedArrivalWhenNoSegments={splitDialog.prefillExpectedArrival}
          hubWritesAllowed={hubWritesAllowed}
          hubReadOnlyTitle={mrpReadOnlyTitle}
          onClose={() => setSplitDialog(null)}
          onSave={(segments) => {
            if (!hubWritesAllowed) return;
            saveDeliveriesMutation.mutate({
              purchaseOrderItemId: splitDialog.purchaseOrderItemId,
              segments,
            });
          }}
          isSaving={saveDeliveriesMutation.isPending}
          saveError={saveDeliveriesMutation.error}
        />
      ) : null}

      {poDateContextMenu ? (
        <>
          <button
            type="button"
            className="fixed inset-0 z-[45] cursor-default bg-transparent"
            aria-label="Close context menu"
            onClick={() => setPoDateContextMenu(null)}
          />
          <div
            className="fixed z-50 min-w-[16rem] max-w-xs rounded-md border border-slate-200 bg-white py-1 text-sm shadow-lg"
            style={{ left: poDateContextMenu.clientX, top: poDateContextMenu.clientY }}
            role="menu"
          >
            <button
              type="button"
              className="block w-full px-3 py-2 text-left text-slate-800 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              role="menuitem"
              disabled={setPurchaseOrderExpectedDateMutation.isPending}
              onClick={() => {
                setPurchaseOrderExpectedDateMutation.mutate({
                  purchaseOrderItemId: poDateContextMenu.purchaseOrderItemId,
                  expectedDate: poDateContextMenu.expectedDate,
                });
              }}
            >
              <span className="block font-medium">Set date for entire PO</span>
              <span className="mt-0.5 block text-xs text-slate-600">
                {poDateContextMenu.poNumber?.trim() !== "" && poDateContextMenu.poNumber != null
                  ? poDateContextMenu.poNumber
                  : "Purchase order"}
                {" · "}
                {poDateContextMenu.expectedDate != null
                  ? new Date(`${poDateContextMenu.expectedDate}T12:00:00`).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })
                  : "No date"}
              </span>
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}

function IncomingSortTh({
  column,
  children,
  align = "left",
  sortHint,
  sortKey: activeKey,
  sortDir,
  onSort,
  className,
}: {
  column: IncomingSortKey;
  children: ReactNode;
  align?: "left" | "right" | "center";
  sortHint?: string;
  sortKey: IncomingSortKey;
  sortDir: "asc" | "desc";
  onSort: (key: IncomingSortKey) => void;
  className?: string;
}) {
  const active = activeKey === column;
  const sortAriaLabel = sortHint != null && sortHint !== "" ? sortHint : "Sort by column";
  const sortButton = (
    <button
      type="button"
      onClick={() => onSort(column)}
      className={cn(
        "group inline-flex w-full min-w-0 items-center gap-0.5 font-inherit text-inherit hover:text-slate-800",
        align === "right" && "justify-end",
        align === "center" && "justify-center",
      )}
      aria-label={sortAriaLabel}
    >
      {children}
      <span
        className={cn("shrink-0 text-[10px] leading-none text-slate-500", !active && "opacity-0 group-hover:opacity-70")}
        aria-hidden
      >
        {active ? (sortDir === "asc" ? "▲" : "▼") : "▲"}
      </span>
    </button>
  );
  return (
    <th
      scope="col"
      className={cn(
        "px-3 py-2 align-top",
        align === "right" && "text-right",
        align === "center" && "text-center",
        className,
      )}
      aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
    >
      {sortHint != null && sortHint !== "" ? (
        <HoverHintTrigger align="start" trigger={sortButton}>
          {sortHint}
        </HoverHintTrigger>
      ) : (
        sortButton
      )}
    </th>
  );
}

function LocationChip({ code }: { code: string }) {
  const { label, className } = mrpLocationChipStyles(code);
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium",
        className,
      )}
    >
      {label}
    </span>
  );
}

function TransferStatusBadge({
  status,
  kind,
}: {
  status: MrpIncomingRow["transfer_status"];
  kind: MrpIncomingRow["kind"];
}) {
  if (status == null) return null;
  const { label, className } =
    kind === "transfer_request"
      ? mrpTransferRequestStatusBadgeStyles(status as MrpTransferRequestStatus)
      : mrpTransferStatusBadgeStyles(status as MrpTransferOrderStatus);
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide",
        className,
      )}
    >
      {label}
    </span>
  );
}

/**
 * Renders the location cell for a transfer-order leg. Paired groups (both ends visible) get a
 * "From → To" chip; solo legs emphasise the visible end with an arrow indicator.
 */
function TransferLocationCell({
  row,
  isPairedTransfer,
}: {
  row: MrpIncomingRow;
  isPairedTransfer: boolean;
}) {
  const fromLabel = row.from_location_label ?? "—";
  const toLabel = row.to_location_label ?? "—";
  if (isPairedTransfer) {
    return (
      <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-800">
        <span>{fromLabel}</span>
        <span className="text-slate-400">→</span>
        <span>{toLabel}</span>
      </span>
    );
  }
  if (row.transfer_role === "from") {
    return (
      <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-800">
        <span className="font-semibold">{fromLabel}</span>
        <span className="text-slate-400">→ {toLabel}</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-800">
      <span className="text-slate-400">{fromLabel} →</span>
      <span className="font-semibold">{toLabel}</span>
    </span>
  );
}

function IncomingTrackingCell({
  value,
  onSave,
  savePending,
  editLabel,
}: {
  value: string | null;
  onSave: ((next: string | null) => void) | undefined;
  savePending: boolean;
  editLabel: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [panelBox, setPanelBox] = useState<AnchoredMenuPanelBox | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const cellClassName = "w-0 whitespace-nowrap px-2 py-2 align-top text-center text-xs";

  useEffect(() => {
    if (!editing) {
      setDraft(value ?? "");
    }
  }, [value, editing]);

  const closeEditor = useCallback((): void => {
    setDraft(value ?? "");
    setEditing(false);
  }, [value]);

  const commit = useCallback((): void => {
    if (onSave == null) {
      setEditing(false);
      return;
    }
    const trimmed = draft.trim();
    const next: string | null = trimmed === "" ? null : trimmed;
    const current = (value ?? "").trim() || null;
    if (next !== current) {
      onSave(next);
    }
    setEditing(false);
  }, [draft, onSave, value]);

  useLayoutEffect(() => {
    if (!editing || triggerRef.current == null) {
      setPanelBox(null);
      return;
    }
    const updatePanelBox = (): void => {
      if (triggerRef.current == null) return;
      setPanelBox(
        computeHorizontalMenuPanel({
          anchorRect: triggerRef.current.getBoundingClientRect(),
          menuWidth: 288,
          horizontalAlign: "end",
        }),
      );
    };
    updatePanelBox();
    window.addEventListener("resize", updatePanelBox);
    window.addEventListener("scroll", updatePanelBox, true);
    return () => {
      window.removeEventListener("resize", updatePanelBox);
      window.removeEventListener("scroll", updatePanelBox, true);
    };
  }, [editing]);

  useEffect(() => {
    if (!editing) return;
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (triggerRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      closeEditor();
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [closeEditor, editing]);

  const display = (value ?? "").trim();
  const trackingHoverHint = display ? value : "No tracking number. Click to add.";

  if (onSave == null) {
    return (
      <td className={cellClassName}>
        {display ? (
          <HoverHintTrigger
            trigger={
              <span className="inline-flex rounded p-0.5 text-brand-600">
                <Truck className="h-3.5 w-3.5" aria-hidden />
              </span>
            }
          >
            {value}
          </HoverHintTrigger>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
    );
  }

  return (
    <td className={cellClassName}>
      <HoverHintTrigger
        trigger={
          <button
            ref={triggerRef}
            type="button"
            onClick={() => setEditing(true)}
            disabled={savePending}
            className={cn(
              "inline-flex shrink-0 rounded p-0.5 hover:bg-slate-100 disabled:opacity-50",
              display ? "text-brand-600 hover:text-brand-800" : "text-slate-400 hover:text-slate-700",
            )}
            aria-label={editLabel}
            aria-expanded={editing}
          >
            <Truck className="h-3.5 w-3.5" aria-hidden />
          </button>
        }
      >
        {trackingHoverHint}
      </HoverHintTrigger>
      {editing && panelBox != null
        ? createPortal(
            <div
              ref={panelRef}
              role="dialog"
              aria-label={editLabel}
              className="fixed rounded-md border border-slate-200 bg-white p-3 shadow-lg"
              style={{
                left: panelBox.left,
                top: panelBox.top,
                width: panelBox.width,
                zIndex: FLOATING_POPOVER_Z_INDEX,
              }}
            >
              <Label htmlFor="incoming-tracking-edit" className="mb-1.5">
                Tracking number
              </Label>
              <Input
                id="incoming-tracking-edit"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    commit();
                  }
                  if (event.key === "Escape") {
                    event.preventDefault();
                    closeEditor();
                  }
                }}
                disabled={savePending}
                autoFocus
                className="h-9 text-xs"
              />
              <div className="mt-2 flex justify-end gap-2">
                <Button size="sm" variant="secondary" type="button" disabled={savePending} onClick={closeEditor}>
                  Cancel
                </Button>
                <SaveChangesButton
                  size="sm"
                  type="button"
                  isDirty={draft.trim() !== (value ?? "").trim()}
                  isLoading={savePending}
                  onClick={commit}
                >
                  Save
                </SaveChangesButton>
              </div>
            </div>,
            document.body,
          )
        : null}
    </td>
  );
}

function IncomingEditableTextCell({
  value,
  onSave,
  savePending,
  editLabel,
  multiline = false,
}: {
  value: string | null;
  onSave: ((next: string | null) => void) | undefined;
  savePending: boolean;
  editLabel: string;
  multiline?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const multilineTextAreaRef = useRef<HTMLTextAreaElement>(null);
  const notesCellClassName = multiline ? "min-w-[10rem]" : "";

  const fitMultilineEditorHeight = useCallback((): void => {
    const element = multilineTextAreaRef.current;
    if (element == null) return;
    element.style.height = "0px";
    element.style.height = `${element.scrollHeight}px`;
  }, []);

  useEffect(() => {
    if (!editing) {
      setDraft(value ?? "");
    }
  }, [value, editing]);

  useLayoutEffect(() => {
    if (!editing || !multiline) return;
    fitMultilineEditorHeight();
  }, [draft, editing, fitMultilineEditorHeight, multiline]);

  if (onSave == null) {
    const text = (value ?? "").trim();
    return (
      <td className={cn("min-w-0 px-3 py-2 align-top text-xs text-slate-600", notesCellClassName)}>
        {text ? (
          <span className={cn(multiline && "whitespace-pre-wrap break-words")}>{value}</span>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
    );
  }

  const commit = (): void => {
    const trimmed = draft.trim();
    const next: string | null = trimmed === "" ? null : trimmed;
    const current = (value ?? "").trim() || null;
    if (next !== current) {
      onSave(next);
    }
    setEditing(false);
  };

  if (editing) {
    if (multiline) {
      return (
        <td className={cn("min-w-0 px-3 py-1 align-top", notesCellClassName)}>
          <TextArea
            ref={multilineTextAreaRef}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={commit}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.preventDefault();
                setDraft(value ?? "");
                setEditing(false);
              }
            }}
            disabled={savePending}
            autoFocus
            rows={1}
            className="min-h-[2.5rem] resize-none overflow-hidden py-2 text-xs font-sans leading-normal"
          />
        </td>
      );
    }
    return (
      <td className="min-w-0 px-3 py-1 align-top">
        <Input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              (event.currentTarget as HTMLInputElement).blur();
            }
            if (event.key === "Escape") {
              setDraft(value ?? "");
              setEditing(false);
            }
          }}
          disabled={savePending}
          autoFocus
          className="h-8 min-w-[8rem] text-xs"
        />
      </td>
    );
  }

  const display = (value ?? "").trim();
  return (
    <td className={cn("min-w-0 px-3 py-2 align-top text-xs text-slate-600", notesCellClassName)}>
      <div className="flex min-w-0 items-start gap-1.5">
        <span className={cn("min-w-0 flex-1", multiline && "whitespace-pre-wrap break-words")}>
          {display ? value : <span className="text-slate-400">—</span>}
        </span>
        <HoverHintTrigger
          trigger={
            <button
              type="button"
              onClick={() => setEditing(true)}
              disabled={savePending}
              className="shrink-0 rounded p-0.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800 disabled:opacity-50"
              aria-label={editLabel}
            >
              <Pencil className="h-3.5 w-3.5" aria-hidden />
            </button>
          }
        >
          {editLabel}
        </HoverHintTrigger>
      </div>
    </td>
  );
}

function IncomingRow({
  row,
  isPairedTransfer,
  hubWritesAllowed,
  hubReadOnlyTitle,
  skuHoverHintTitle,
  onDeleteNonPo,
  onDeleteTransfer,
  deleteTransferPending,
  onUpdateDate,
  onOpenSplitDeliveries,
  datePending,
  dateCommitError,
  onSaveNotes,
  onSaveTracking,
  metadataSavePending,
  metadataSaveVariables,
  onDateContextMenu,
}: {
  row: MrpIncomingRow;
  isPairedTransfer: boolean;
  hubWritesAllowed: boolean;
  hubReadOnlyTitle: string;
  skuHoverHintTitle: string;
  onDeleteNonPo: () => void;
  onDeleteTransfer: () => void;
  deleteTransferPending: boolean;
  onUpdateDate: (value: string | null) => void;
  onOpenSplitDeliveries?: () => void;
  datePending: boolean;
  dateCommitError?: string | null;
  onSaveNotes: ((notes: string | null) => void) | undefined;
  onSaveTracking: ((tracking_number: string | null) => void) | undefined;
  metadataSavePending: boolean;
  metadataSaveVariables:
    | IncomingRowMetadataVariables
    | undefined;
  onDateContextMenu?: (event: MouseEvent<HTMLInputElement>, draft: string) => void;
}) {
  const metadataRowPending =
    metadataSavePending &&
    metadataSaveVariables != null &&
    matchesIncomingMetadataTarget(row, metadataSaveVariables);
  const notesSavePending = metadataRowPending && metadataSaveVariables?.notes !== undefined;
  const trackingSavePending =
    metadataRowPending && metadataSaveVariables?.tracking_number !== undefined;
  const workOrderExpectedDateLockedByBatch =
    row.kind === "work_order" &&
    row.work_order_batch_mfr_ship_by != null &&
    row.work_order_batch_mfr_ship_by.trim() !== "";
  const canEditExpectedDate =
    row.kind === "po" ||
    row.kind === "non_po" ||
    (row.kind === "work_order" && !workOrderExpectedDateLockedByBatch) ||
    (row.kind === "transfer_request" && isMrpTransferRequestEditable(row.transfer_status));
  const expectedDateReadOnlyHoverHint = workOrderExpectedDateLockedByBatch
    ? "This expected date comes from the manufacturing batch manufacturer ship-by date. Change it on the batch to edit it here."
    : hubReadOnlyTitle;
  const remaining = row.quantity_remaining;
  const arrivalForInput = expectedArrivalForDateInput(row.expected_arrival);
  const noDate = !arrivalForInput;

  // For paired transfer groups the renderer keeps the "to" leg only and shows the absolute
  // quantity with a "From → To" chip; the source leg is hidden by the parent.
  const displayQuantity =
    isMrpIncomingTransferKind(row.kind) && isPairedTransfer ? Math.abs(remaining) : remaining;

  return (
    <tr className={cn("transition-colors hover:bg-slate-100", noDate && "bg-amber-50/40")}>
      <td className="min-w-0 overflow-hidden px-3 py-2 align-top font-mono text-sm" title={row.cpn}>
        <HoverHintContent hoverHint={skuHoverHintTitle} block>
          <MrpSkuDetailLink
            sku={row.cpn}
            className="block truncate font-semibold text-brand-700 hover:text-brand-900"
          >
            {row.cpn}
          </MrpSkuDetailLink>
        </HoverHintContent>
      </td>
      <td className="min-w-0 px-3 py-2 align-top text-xs text-slate-700">
        {row.supplier != null && row.supplier.trim() !== "" ? (
          <span className="break-words" title={row.supplier}>
            {row.supplier}
          </span>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="min-w-0 px-3 py-2 align-top">
        {isMrpIncomingTransferKind(row.kind) ? (
          <TransferLocationCell row={row} isPairedTransfer={isPairedTransfer} />
        ) : (
          <LocationChip code={row.location_code} />
        )}
      </td>
      <td className="min-w-0 whitespace-nowrap px-3 py-2 align-top text-right font-mono text-xs">
        {(row.kind === "po" || row.kind === "work_order") && row.quantity_ordered != null ? (
          <span>
            {formatNumber(remaining)}/{formatNumber(row.quantity_ordered)}
          </span>
        ) : (
          formatNumber(displayQuantity)
        )}
      </td>
      <td className="min-w-0 px-3 py-2 align-top text-xs">
        <div className="flex min-w-0 flex-wrap items-center gap-1">
          <ReadOnlyHoverLayer
            active={(!hubWritesAllowed && canEditExpectedDate) || workOrderExpectedDateLockedByBatch}
            hoverHint={expectedDateReadOnlyHoverHint}
            layout="inline"
          >
            <DeferredCommitDateInput
              value={arrivalForInput}
              disabled={!canEditExpectedDate || !hubWritesAllowed}
              pending={datePending}
              commitError={dateCommitError}
              onCommit={onUpdateDate}
              onContextMenu={onDateContextMenu}
              className={cn(
                "h-7 w-36 text-xs",
                noDate && canEditExpectedDate && "border-amber-400 bg-white",
                !canEditExpectedDate && "cursor-not-allowed bg-slate-50 text-slate-500",
              )}
            />
          </ReadOnlyHoverLayer>
          {row.kind === "transfer_order" ? (
            <span className="text-[10px] text-slate-500">
              {row.transfer_status === "shipped" || row.transfer_status === "delivered" || row.transfer_status === "partially_received"
                ? "Shipped"
                : "Ship by"}
            </span>
          ) : row.kind === "transfer_request" ? (
            <span className="text-[10px] text-slate-500">Target</span>
          ) : null}
        </div>
      </td>
      <td className="min-w-0 px-3 py-2 align-top text-xs">
        {row.kind === "po" ? (
          <div className="space-y-0.5">
            <span className="inline-flex rounded-md border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-900">
              PO
            </span>
            <div className="flex flex-wrap items-center gap-1.5 font-mono">
              {incomingPoNumberCell(row)}
            </div>
            <IncomingDocumentStatusBadge label={poIncomingDetailStatusLabel(row)} />
            {row.allocated_stock != null && row.allocated_stock > 0 ? (
              <div className="text-slate-500">{formatNumber(row.allocated_stock)} allocated</div>
            ) : null}
          </div>
        ) : row.kind === "work_order" ? (
          <div className="space-y-0.5">
            <span className="inline-flex rounded-md border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-900">
              Work order
            </span>
            <div className="flex flex-wrap items-center gap-1.5 font-mono">
              <span>{row.po_number ?? <span className="italic text-slate-400">no work order number</span>}</span>
              {row.work_order_mv_id != null ? (
                <a
                  href={megaventoryWorkOrderDocumentUrl(row.work_order_mv_id, row.po_number)}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex shrink-0 text-slate-400 hover:text-brand-600"
                  aria-label={`Open work order ${row.po_number ?? ""} in Megaventory`}
                >
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                </a>
              ) : null}
            </div>
            <IncomingDocumentStatusBadge label={workOrderIncomingDetailStatusLabel(row)} />
          </div>
        ) : row.kind === "transfer_request" ? (
          <div className="space-y-0.5">
            <span className="inline-flex rounded-md border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-900">
              Transfer request
            </span>
            <div className="font-mono">
              {row.po_number ?? <span className="italic text-slate-400">no request id</span>}
            </div>
            <TransferStatusBadge status={row.transfer_status} kind={row.kind} />
          </div>
        ) : row.kind === "transfer_order" ? (
          <div className="space-y-0.5">
            <span className="inline-flex rounded-md border border-indigo-200 bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-900">
              Transfer
            </span>
            <div className="font-mono">
              {row.transfer_number ?? <span className="italic text-slate-400">no T-#</span>}
            </div>
            <TransferStatusBadge status={row.transfer_status} kind={row.kind} />
          </div>
        ) : isMrpIncomingPartGhostSource(row.source) ? (
          <div className="space-y-0.5">
            <span className="inline-flex rounded-md border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-900">
              Ghost number
            </span>
            {row.notes?.trim() ? <div className="text-xs text-slate-600">{row.notes}</div> : null}
          </div>
        ) : (
          <div>{row.source ?? <span className="italic text-slate-400">manual</span>}</div>
        )}
      </td>
      <IncomingTrackingCell
        value={row.tracking_number}
        onSave={onSaveTracking}
        savePending={trackingSavePending}
        editLabel="Edit tracking number"
      />
      <IncomingEditableTextCell
        value={row.notes}
        onSave={onSaveNotes}
        savePending={notesSavePending}
        editLabel="Edit notes"
        multiline
      />
      <td className="w-[7.75rem] min-w-[7.75rem] whitespace-nowrap px-3 py-2 align-top text-right">
        <div className="flex flex-col items-end gap-1">
          {row.kind === "po" && onOpenSplitDeliveries ? (
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={hubReadOnlyTitle} layout="inline">
              <Button
                size="sm"
                variant="secondary"
                type="button"
                disabled={!hubWritesAllowed}
                onClick={onOpenSplitDeliveries}
                className="h-auto min-h-8 whitespace-nowrap py-1.5 leading-snug"
              >
                Split delivery
              </Button>
            </ReadOnlyHoverLayer>
          ) : null}
          {row.kind === "non_po" ? (
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={hubReadOnlyTitle} layout="inline">
              <Button size="sm" variant="danger" disabled={!hubWritesAllowed} onClick={onDeleteNonPo}>
                Delete
              </Button>
            </ReadOnlyHoverLayer>
          ) : null}
          {row.kind === "transfer_order" && isMrpTransferOrderEditable(row.transfer_status) ? (
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={hubReadOnlyTitle} layout="inline">
              <Button
                size="sm"
                variant="danger"
                onClick={onDeleteTransfer}
                isLoading={deleteTransferPending}
                disabled={!hubWritesAllowed}
              >
                Delete
              </Button>
            </ReadOnlyHoverLayer>
          ) : row.kind === "transfer_order" ? (
            <span className="text-[10px] text-slate-400">In workflow</span>
          ) : row.kind === "transfer_request" ? (
            <Link
              to="/manufacturing-management/transfers"
              className="text-[10px] font-medium text-brand-700 hover:text-brand-900"
            >
              Convert on Transfers
            </Link>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

interface SplitDeliveriesDialogProps {
  purchaseOrderItemId: number;
  cpn: string;
  /** When the line has no `mrp.purchase_order_item_delivery` rows yet, seed the first segment from the table row. */
  prefillExpectedArrivalWhenNoSegments: string | null;
  hubWritesAllowed: boolean;
  hubReadOnlyTitle: string;
  onClose: () => void;
  onSave: (segments: { quantity: number; expected_arrival: string | null }[]) => void;
  isSaving: boolean;
  saveError: Error | ApiError | null;
}

function SplitDeliveriesDialog({
  purchaseOrderItemId,
  cpn,
  prefillExpectedArrivalWhenNoSegments,
  hubWritesAllowed,
  hubReadOnlyTitle,
  onClose,
  onSave,
  isSaving,
  saveError,
}: SplitDeliveriesDialogProps) {
  const planQuery = useQuery({
    queryKey: ["mrp", "po-delivery-plan", purchaseOrderItemId],
    queryFn: () =>
      apiFetch<{
        order_quantity: number;
        quantity_received: number;
        line_remaining: number;
        segments: { id: number; quantity: number; expected_arrival: string | null; sort_order: number }[];
      }>(`/api/mrp/mv-purchase-order-items/${purchaseOrderItemId}/delivery-plan`),
  });

  const [draft, setDraft] = useState<{ quantity: string; expected_arrival: string }[]>([]);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!planQuery.data) return;
    const { order_quantity: orderQuantity, segments } = planQuery.data;
    if (segments.length === 0) {
      setDraft([
        {
          quantity: String(orderQuantity),
          expected_arrival: expectedArrivalForDateInput(prefillExpectedArrivalWhenNoSegments),
        },
      ]);
    } else {
      setDraft(
        segments.map((segment) => ({
          quantity: String(segment.quantity),
          expected_arrival: segment.expected_arrival
            ? expectedArrivalForDateInput(segment.expected_arrival)
            : "",
        })),
      );
    }
    setLocalError(null);
  }, [planQuery.data, purchaseOrderItemId, prefillExpectedArrivalWhenNoSegments]);

  const orderQty = planQuery.data?.order_quantity ?? 0;
  const lineRemaining = planQuery.data?.line_remaining ?? 0;

  const splitQuantitySummary = useMemo(() => {
    if (!planQuery.data) {
      return {
        perLine: [] as { value: number | null; hasInvalid: boolean }[],
        totalAllocated: 0,
        canSave: false,
        leftToAssign: 0,
        isBalanced: false,
        hasOver: false,
        hasEmptyLine: false,
        hasInvalidLine: false,
        allPositive: false,
      };
    }
    const target = planQuery.data.order_quantity;
    const perLine: { value: number | null; hasInvalid: boolean }[] = draft.map((line) => {
      const t = line.quantity.trim();
      if (t === "") return { value: null, hasInvalid: false };
      const n = parseSegmentQuantityInput(line.quantity);
      if (n == null) return { value: null, hasInvalid: true };
      return { value: n, hasInvalid: false };
    });

    const hasInvalidLine = perLine.some((p) => p.hasInvalid);
    const hasEmptyLine = perLine.some((p) => p.value == null && !p.hasInvalid);
    let totalAllocated = 0;
    for (const p of perLine) {
      if (p.value != null) totalAllocated += p.value;
    }

    const allPositive = perLine.length > 0 && perLine.every((p) => p.value != null && p.value > 0);
    const isBalanced = Math.abs(totalAllocated - target) < 1e-3;
    const hasOver = totalAllocated - target > 1e-3;
    const leftToAssign = target - totalAllocated;
    const canSave =
      !hasInvalidLine && !hasEmptyLine && allPositive && isBalanced;

    return {
      perLine,
      totalAllocated,
      canSave,
      leftToAssign,
      isBalanced,
      hasOver,
      hasEmptyLine,
      hasInvalidLine,
      allPositive,
    };
  }, [planQuery.data, draft]);

  const saveMessage =
    saveError instanceof ApiError ? saveError.message : saveError != null ? saveError.message : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="split-delivery-title"
    >
      <Card className="max-h-[90vh] w-full max-w-lg overflow-y-auto shadow-xl">
        <CardHeader>
          <h3 id="split-delivery-title" className="text-base font-semibold text-slate-900">
            Split delivery dates
          </h3>
        </CardHeader>
        <CardBody className="space-y-3">
          <p className="text-sm text-slate-600">
            <span className="font-mono">{cpn}</span>
            {" · "}
            Order quantity <span className="font-mono">{formatNumber(orderQty)}</span>
            {planQuery.data ? (
              <>
                {" "}
                ({formatNumber(planQuery.data.quantity_received)} received,{" "}
                <span className="font-mono">{formatNumber(lineRemaining)}</span> still open on the line). All segment
                quantities must add up to that order quantity. Receipts apply to segments in order (first segment first).
                Leave a date empty when unknown.
              </>
            ) : null}
          </p>
          {planQuery.isPending ? <p className="text-sm text-slate-500">Loading…</p> : null}
          {planQuery.isError ? (
            <p className="text-sm text-rose-700">{(planQuery.error as Error).message}</p>
          ) : null}
          {!planQuery.isPending && planQuery.data ? (
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={hubReadOnlyTitle} layout="block">
            <div className="space-y-2">
              {draft.map((line, index) => {
                const p = splitQuantitySummary.perLine[index]!;
                return (
                <div
                  key={index}
                  className={cn(
                    "grid items-end gap-x-2 border-b border-slate-100 pb-2",
                    draft.length > 1
                      ? "grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)_2.5rem]"
                      : "grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]",
                  )}
                >
                  <div className="min-w-0">
                    <Label>Quantity</Label>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={line.quantity}
                      disabled={!hubWritesAllowed}
                      onChange={(event) => {
                        setLocalError(null);
                        setDraft((current) => {
                          const next = [...current];
                          next[index] = { ...next[index]!, quantity: event.target.value };
                          return next;
                        });
                      }}
                      className={cn(
                        "h-9 text-xs",
                        p.hasInvalid && "border-rose-500",
                      )}
                    />
                  </div>
                  <div className="min-w-0">
                    <Label>Expected arrival</Label>
                    <Input
                      type="date"
                      value={line.expected_arrival}
                      disabled={!hubWritesAllowed}
                      onChange={(event) => {
                        const next = [...draft];
                        next[index] = { ...next[index]!, expected_arrival: event.target.value };
                        setDraft(next);
                      }}
                      className="h-9 text-xs"
                    />
                  </div>
                  {draft.length > 1 ? (
                    <div className="flex h-9 items-center justify-end">
                      <button
                        type="button"
                        disabled={!hubWritesAllowed}
                        onClick={() => {
                          setDraft((current) => current.filter((_, rowIndex) => rowIndex !== index));
                        }}
                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                        aria-label="Remove segment"
                      >
                        <X className="h-4 w-4" strokeWidth={2} />
                      </button>
                    </div>
                  ) : null}
                </div>
                );
              })}
              {draft.length > 0 ? (
                <div className="border-b border-slate-200 bg-slate-50/60 -mx-1 rounded px-2 py-2">
                  <p
                    className={cn(
                      "text-sm font-mono",
                      splitQuantitySummary.canSave
                        ? "text-emerald-800"
                        : splitQuantitySummary.hasInvalidLine || splitQuantitySummary.hasOver
                          ? "text-rose-800"
                          : splitQuantitySummary.leftToAssign > 1e-3
                            ? "text-amber-800"
                            : "text-slate-800",
                    )}
                  >
                    <span className="font-sans text-xs font-medium text-slate-600">Total quantity </span>
                    {formatNumber(splitQuantitySummary.totalAllocated)} / {formatNumber(orderQty)}
                    {splitQuantitySummary.hasInvalidLine
                      ? " (invalid number)"
                      : splitQuantitySummary.hasOver
                        ? ` (${formatNumber(splitQuantitySummary.totalAllocated - orderQty)} over)`
                        : splitQuantitySummary.leftToAssign > 1e-3
                          ? ` (${formatNumber(splitQuantitySummary.leftToAssign)} left)`
                          : splitQuantitySummary.isBalanced && splitQuantitySummary.canSave
                            ? ""
                            : splitQuantitySummary.isBalanced && (splitQuantitySummary.hasEmptyLine || !splitQuantitySummary.allPositive)
                              ? " (incomplete rows)"
                              : " (does not match)"}
                  </p>
                </div>
              ) : null}
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  type="button"
                  disabled={!hubWritesAllowed}
                  onClick={() => {
                    setLocalError(null);
                    setDraft((current) => [...current, { quantity: "", expected_arrival: "" }]);
                  }}
                >
                  Add segment
                </Button>
              </div>
            </div>
            </ReadOnlyHoverLayer>
          ) : null}
          {localError ? <p className="text-sm text-rose-700">{localError}</p> : null}
          {saveMessage ? <p className="text-sm text-rose-700">{saveMessage}</p> : null}
          <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-3">
            <Button size="sm" variant="secondary" type="button" disabled={isSaving} onClick={onClose}>
              Cancel
            </Button>
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={hubReadOnlyTitle} layout="inline">
            <Button
              size="sm"
              variant="primary"
              type="button"
              isLoading={isSaving}
              disabled={
                !hubWritesAllowed ||
                planQuery.isPending ||
                planQuery.isError ||
                !splitQuantitySummary.canSave
              }
              onClick={() => {
                if (!hubWritesAllowed) return;
                const parsed = draft.map((line) => ({
                  quantity: Number.parseFloat(line.quantity.replace(/,/g, "")),
                  expected_arrival: line.expected_arrival.trim() === "" ? null : line.expected_arrival.trim(),
                }));
                for (const item of parsed) {
                  if (!Number.isFinite(item.quantity) || item.quantity <= 0) {
                    setLocalError("Each segment needs a positive quantity.");
                    return;
                  }
                }
                const sum = parsed.reduce((accumulator, item) => accumulator + item.quantity, 0);
                const target = planQuery.data?.order_quantity ?? 0;
                if (Math.abs(sum - target) > 1e-3) {
                  setLocalError(`Segment quantities must sum to order quantity (${formatNumber(target)}).`);
                  return;
                }
                setLocalError(null);
                onSave(parsed);
              }}
            >
              Save
            </Button>
            </ReadOnlyHoverLayer>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
